import { Box } from "@mui/material";
import { themeColors } from "../../utils/constants";

export const CACHE_KEY = "xtwitter:pulledTweets";
export const KEYWORD_LIST_CONFIG = "x_search_keywords";
export const KEYWORD_SUFFIX_CONFIG = "x_search_suffix";
export const LEGACY_QUERY_CONFIG = "x_search_query";
export const MIN_FOLLOWER_CONFIG = "x_min_follower_count";
export const DEFAULT_SUFFIX = "lang:en -is:retweet -is:reply";

export const textFieldStyles = {
  "& .MuiInputBase-input": { color: themeColors.color },
  "& .MuiFormLabel-root": { color: themeColors.color },
  "& .MuiOutlinedInput-root": {
    color: themeColors.color,
    "& fieldset": { borderColor: themeColors.color },
    "&:hover fieldset": { borderColor: themeColors.color },
    "&.Mui-focused fieldset": { borderColor: themeColors.color },
  },
};

export const gqlFetch = async (query, variables) => {
  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors.map((e) => e.message).join("; "));
  return json.data;
};

export function readCachedRows() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("Failed to read cached tweets", err);
    try {
      window.localStorage.removeItem(CACHE_KEY);
    } catch (cleanupErr) {
      console.warn("Failed to remove corrupt tweet cache", cleanupErr);
    }
    return [];
  }
}

export function persistRows(rowsToSave) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(rowsToSave));
  } catch (err) {
    console.warn("Failed to persist tweets cache", err);
  }
}

export function normalizeKeyword(value) {
  return (value || "").trim().replace(/\s+/g, " ").toUpperCase();
}

export function parseKeywordsFromConfig(keywordValue, legacyValue) {
  if (keywordValue) {
    try {
      const parsed = JSON.parse(keywordValue);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => normalizeKeyword(String(item || "")))
          .filter(Boolean);
      }
    } catch (err) {
      // ignore JSON parsing failures
    }
  }
  const legacy = deriveLegacyParts(legacyValue);
  if (legacy.keywordsText) {
    return legacy.keywordsText
      .split(/OR|,|\|/i)
      .map((part) =>
        normalizeKeyword(part.replace(/^[()\s]+|[()\s]+$/g, ""))
      )
      .filter(Boolean);
  }
  return [];
}

export function parseSuffixFromConfig(suffixValue, legacyValue) {
  if (suffixValue && suffixValue.trim().length > 0) {
    return suffixValue.trim();
  }
  const legacy = deriveLegacyParts(legacyValue);
  if (legacy.suffixText) return legacy.suffixText;
  return DEFAULT_SUFFIX;
}

function deriveLegacyParts(raw) {
  if (!raw) return { keywordsText: "", suffixText: "" };
  try {
    const match = raw.match(/^\s*\(([^)]+)\)\s*(.*)$/);
    if (match) {
      return {
        keywordsText: match[1] || "",
        suffixText: (match[2] || "").trim(),
      };
    }
  } catch (err) {
    // ignore parsing issues and fall back
  }
  return { keywordsText: raw, suffixText: "" };
}

// Design tokens for the X reply queue (design_handoff_x_reply_queue).
// The app has no MUI palette override carrying these values, so they live here
// as the single source rather than being repeated as hex across components.
export const X_QUEUE = {
  card: "#1b1b1b",
  cardHover: "#242424",
  input: "#0e0e0e",
  divider: "#2a2a2a",
  control: "#3a3a3a",
  inputBorder: "#333",
  separator: "#4a4a4a",
  textPrimary: "#e8e8e8",
  tweetBody: "#d5d5d5",
  textSecondary: "#9a9a9a",
  textTertiary: "#7a7a7a",
  textDisabled: "#6a6a6a",
  disabledBg: "#262626",
  accent: "#4da3ff",
  primaryButton: "#1f7ae0",
  primaryButtonHover: "#2a86ee",
  success: "#5ec98a",
  warning: "#e0b34d",
  danger: "#ff6b6b",
  dangerBg: "#2a1a1a",
  postedBorder: "#1f3a5a",
  mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};

// Surface, control and badge styling shared by the X admin tabs so the
// configuration and history views read as the same system as the reply queue.
export const xPanelSx = {
  backgroundColor: X_QUEUE.card,
  border: "1px solid",
  borderColor: X_QUEUE.divider,
  borderRadius: "8px",
  p: "20px",
};

export const xPanelTitleSx = {
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: ".06em",
  color: X_QUEUE.textPrimary,
};

export const xFieldLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: ".04em",
  color: X_QUEUE.textSecondary,
};

export const xHintSx = {
  fontSize: 12,
  lineHeight: 1.5,
  color: X_QUEUE.textTertiary,
};

export const xFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: X_QUEUE.input,
    borderRadius: "6px",
    color: X_QUEUE.textPrimary,
    fontSize: 14,
    lineHeight: 1.5,
    "& fieldset": { borderColor: X_QUEUE.inputBorder },
    "&:hover fieldset": { borderColor: X_QUEUE.control },
    "&.Mui-focused fieldset": { borderColor: X_QUEUE.accent, borderWidth: "1px" },
    "&.Mui-disabled fieldset": { borderColor: X_QUEUE.inputBorder },
    p: "12px",
  },
  "& .MuiOutlinedInput-input": { p: 0 },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: X_QUEUE.textDisabled,
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: X_QUEUE.textTertiary,
    opacity: 1,
  },
  "& .MuiSelect-icon": { color: X_QUEUE.textSecondary },
};

export const xButtonSx = {
  height: 36,
  px: "14px",
  borderRadius: "6px",
  border: "1px solid",
  borderColor: X_QUEUE.control,
  backgroundColor: X_QUEUE.card,
  color: X_QUEUE.textPrimary,
  fontSize: 14,
  fontWeight: 500,
  textTransform: "none",
  whiteSpace: "nowrap",
  "&:hover": { backgroundColor: X_QUEUE.cardHover, borderColor: X_QUEUE.control },
  "&.Mui-disabled": { color: X_QUEUE.textDisabled, borderColor: X_QUEUE.control },
};

export const X_STATUS_BADGES = {
  empty: { label: "No draft", color: X_QUEUE.textTertiary, bg: "#1e1e1e" },
  draft: { label: "Unsaved", color: X_QUEUE.warning, bg: "#2a2410" },
  saved: { label: "Ready", color: X_QUEUE.success, bg: "#12291b" },
  pending: { label: "Pending", color: X_QUEUE.warning, bg: "#2a2410" },
  handed_off: { label: "Handed off", color: X_QUEUE.accent, bg: "#10223a" },
  posted: { label: "Posted", color: X_QUEUE.accent, bg: "#10223a" },
  denied: { label: "Denied", color: X_QUEUE.danger, bg: X_QUEUE.dangerBg },
  na: { label: "NA", color: X_QUEUE.warning, bg: "#2a2410" },
  skipped: { label: "Skipped", color: X_QUEUE.danger, bg: X_QUEUE.dangerBg },
  removed: { label: "Removed", color: X_QUEUE.textTertiary, bg: "#1e1e1e" },
};

export const xBadgeSx = (state) => ({
  display: "inline-block",
  fontSize: 12,
  fontWeight: 600,
  px: "8px",
  py: "3px",
  borderRadius: "4px",
  color: X_STATUS_BADGES[state].color,
  backgroundColor: X_STATUS_BADGES[state].bg,
});

// Dot + text status line, matching the flash message in the reply queue header.
export const XStatusNote = ({ message, tone = "info" }) => {
  if (!message) return null;
  const dotColor = { info: X_QUEUE.accent, success: X_QUEUE.success, error: X_QUEUE.danger }[tone];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        fontSize: 13,
        color: tone === "error" ? X_QUEUE.danger : X_QUEUE.textSecondary,
      }}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: dotColor,
          flexShrink: 0,
        }}
      />
      {message}
    </Box>
  );
};

const NAMED_ENTITIES = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

export function decodeHtmlEntities(value) {
  return (value || "").replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity) => {
    if (entity[0] !== "#") {
      const named = NAMED_ENTITIES[entity.toLowerCase()];
      return named === undefined ? match : named;
    }
    const isHex = entity[1] === "x" || entity[1] === "X";
    const codePoint = isHex
      ? parseInt(entity.slice(2), 16)
      : parseInt(entity.slice(1), 10);
    return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
  });
}
